import React, { useState, useMemo, useEffect } from 'react';
import { Box, Button, HStack, Select, VStack, Text as ChakraText, } from '@chakra-ui/react';
import Icon from '../../../../../components/content/icon';
import PopOver from '../../../../../components/surfaces/pop-over/index.web';
import BaseText from '../../../../../components/content/text';
import { Pressable } from 'react-native';
import COLORS from '../../../../../constants/colors';
import moment from 'moment';
import { DateFormat, formatDate } from '../../../../../utils/date';
// Helper to generate year options
const generateYearOptions = (range = 10) => {
    const currentYear = moment().year();
    const years = [];
    for (let i = currentYear + range; i >= currentYear - range; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};
// Helper for month options
const monthOptions = moment.months().map((month, index) => ({
    value: index,
    label: month,
}));
export const FilterDate = ({ date, handleOnDateChange, togglePopover, onTogglePopOver, textDate, }) => {
    // Internal state for month/year selection within the popover
    const [selectedYear, setSelectedYear] = useState(date ? moment(date).year() : null);
    const [selectedMonth, setSelectedMonth] = useState(date ? moment(date).month() : null);
    const yearOptions = useMemo(() => generateYearOptions(), []);
    // Update internal state if the external date changes
    useEffect(() => {
        setSelectedYear(date ? moment(date).year() : null);
        setSelectedMonth(date ? moment(date).month() : null);
    }, [date]);
    const handleApply = () => {
        if (selectedYear !== null) {
            const newMoment = moment().year(selectedYear);
            if (selectedMonth !== null) {
                newMoment.month(selectedMonth);
            }
            let newDate;
            // Set specific date based on "From" or "To" context
            if (textDate === 'From') {
                // Assuming textDate prop reliably indicates this
                newDate = newMoment
                    .startOf(selectedMonth !== null ? 'month' : 'year')
                    .toDate();
            }
            else {
                // Assuming "To"
                newDate = newMoment
                    .endOf(selectedMonth !== null ? 'month' : 'year')
                    .toDate();
            }
            handleOnDateChange(newDate);
        }
        else {
            // If year is cleared, clear the date
            handleOnDateChange(null);
        }
        onTogglePopOver(); // Close popover on apply
    };
    const handleClear = () => {
        setSelectedYear(null);
        setSelectedMonth(null);
        handleOnDateChange(null); // Also clear the main date state
        onTogglePopOver(); // Close popover
    };
    // Display MM/YYYY or YYYY if date is set
    const displayDateString = useMemo(() => {
        if (!date)
            return null;
        const mDate = moment(date);
        // Check if it's start/end of month/year to infer selection type
        const isMonthSelected = mDate.isSame(mDate.clone().startOf('month'), 'day') ||
            mDate.isSame(mDate.clone().endOf('month'), 'day');
        const isYearSelectedOnly = mDate.isSame(mDate.clone().startOf('year'), 'day') ||
            mDate.isSame(mDate.clone().endOf('year'), 'day');
        if (isMonthSelected && !isYearSelectedOnly)
            return mDate.format('MM/YYYY');
        if (isYearSelectedOnly)
            return mDate.format('YYYY');
        // Fallback to standard short format if it's a specific day
        return formatDate(date, DateFormat.ShortFormat, '');
    }, [date]);
    return (<PopOver from={<HStack as="button" onClick={onTogglePopOver} border="1px solid #E7E9F4" spacing={3} align="center" backgroundColor={COLORS.White.T500.value} color={COLORS.Text.T400.value} borderRadius="8px" paddingX={3} paddingY={0} height={'38px'} minHeight={'38px'} _hover={{ borderColor: COLORS.Text.T300.value }} transition="border-color 0.2s ease-in-out">
          <BaseText color={date ? COLORS.Text.T400.value : COLORS.Text.T300.value}>
            {textDate}:{' '}
          </BaseText>
          {displayDateString && (<HStack spacing={2} backgroundColor={COLORS.Govrn.Blue.T100.value} paddingX="0.5rem" paddingY="0.125rem" borderRadius="0.125rem" alignItems="center">
              <ChakraText fontSize="sm">{displayDateString}</ChakraText>
              <Pressable onPress={e => {
                    e.stopPropagation();
                    handleClear();
                }} aria-label={`Clear ${textDate} date`}>
                <Icon name="close-cross" width={12} height={12} fill={COLORS.Text.T500.value}/>
              </Pressable>
            </HStack>)}
          {/* Wrap icon in Box for alignment */}
          <Box ml="auto">
            <Icon name="calendar" width="1rem" stroke={COLORS.Text.T300.value}/>
          </Box>
        </HStack>} placement="bottom-start" isOpen={togglePopover} onToggle={onTogglePopOver} isPressable={false}>
      <VStack p={4} spacing={4} bg={COLORS.White.T500.value} borderRadius="md" boxShadow="md">
        <HStack width="100%" spacing={3}>
          <Select placeholder="Month" value={selectedMonth ?? ''} onChange={e => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)} flex={1}>
            {monthOptions.map(opt => (<option key={opt.value} value={opt.value}>
                {opt.label}
              </option>))}
          </Select>
          <Select placeholder="Year" value={selectedYear ?? ''} onChange={e => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)} flex={1}>
            {yearOptions.map(opt => (<option key={opt.value} value={opt.value}>
                {opt.label}
              </option>))}
          </Select>
        </HStack>
        <HStack width="100%" justify="space-between">
          <Button size="sm" variant="ghost" onClick={handleClear}>
            Clear
          </Button>
          <Button size="sm" colorScheme="blue" onClick={handleApply} isDisabled={selectedYear === null}>
            Apply
          </Button>
        </HStack>
      </VStack>
    </PopOver>);
};
